import { LIFECYCLE_CATEGORIES, LIFECYCLE_TILES } from '@cdf/navigation-catalogue';
import { getStandardNavigationCatalogue, navigationByMode, type NavSection } from '@/lib/navigation';

// Search metadata only: never use this catalogue to create visible results.
const keywords: Readonly<Record<string, string>> = {
  'acquisition.agent': 'assistant chat prompts conversation',
  'overview.daily-briefing': 'ai summary today digest agent briefing insights',
  'acquisition.outlook': 'personal inbox mailbox email',
  'acquisition.shared-mailbox': 'shared group team inbox mailbox email outlook',
  'crm.icloud': 'apple personal address book',
  'overview.lifecycle': 'workflow stages find engage develop onboard serve retain grow govern improve',
  'crm.accounts': 'customers consumers organisations organizations companies investors directory relationships',
  'crm.contacts': 'people interaction history relationships',
  'crm.opportunities': 'pipeline deals consumer funnel funding rounds joint opportunities relationships',
  'crm.quotes': 'relationships quotations',
  'crm.tenders': 'relationships',
  'crm.sows': 'relationships',
  'partner.overview': 'relationships',
  'partner.partners': 'relationships',
  'crm.enquiries': 'inquiries incoming requests',
  'crm.opportunity-hub': 'leads inbox mailbox all opportunities social leads web signals referrals quote requests pipeline',
  'overview.next-actions': 'follow up priorities next actions relationship health',
  'overview.geography': 'geographic locations map',
  'files.workspace': 'documents library attachments',
  'content-hub.knowledge': 'sources evidence',
  'content-hub.posts': 'social posts drafts publication',
  'content-hub.calendar': 'scheduled posts',
  'content-hub.mailing-list': 'campaigns subscribers email',
  'research.workspace': 'threads research documents',
  'admin.entity': 'entity configuration setup',
  'tenant.admin': 'tenant configuration setup',
  'platform.admin': 'platform configuration setup'
};
const catalogue = getStandardNavigationCatalogue();
const canonical = new Map(catalogue.map(item => [item.key, item.defaultTitle]));
const routeKeys = new Map(catalogue.map(item => [item.to, item.key]));
const audienceTitles = new Map<string, Set<string>>();
for (const sections of Object.values(navigationByMode)) {
  for (const item of sections.flatMap(section => section.items)) {
    if (!item.key) continue;
    const titles = audienceTitles.get(item.key) ?? new Set<string>();
    titles.add(item.title);
    audienceTitles.set(item.key, titles);
  }
}
const normalize = (text: string) => text.normalize('NFKC').toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
const lifecycleTerms = new Map<string, string[]>();
for (const tile of LIFECYCLE_TILES) {
  for (const binding of tile.bindings) {
    if (binding.state !== 'existing') continue;
    const terms = lifecycleTerms.get(binding.navigationKey) ?? [];
    terms.push(tile.description, LIFECYCLE_CATEGORIES.find(category => category.id === tile.category)!.title);
    lifecycleTerms.set(binding.navigationKey, terms);
  }
}
function matches(title: string, key: string | undefined, heading: string, terms: string[]) {
  const text = normalize([heading, title, key ? canonical.get(key) : '', key ? keywords[key] : '',
    ...(key ? audienceTitles.get(key) ?? [] : []), ...(key ? lifecycleTerms.get(key) ?? [] : [])].join(' '));
  return terms.every(term => text.includes(term));
}

/** Input MUST be the access/module/layout-resolved tree. Never searches hidden catalogue entries. */
export function filterNavigationSections<T extends NavSection>(sections: T[], query: string): T[] {
  const normalized = normalize(query);
  if (!query.trim()) return sections;
  if (!normalized) return [];
  const terms = normalized.split(' ');
  return sections.flatMap(section => {
    const items = section.items.flatMap(item => {
      if (matches(item.title, item.key ?? (item.to ? routeKeys.get(item.to) : undefined), section.heading, terms)) return [item];
      const children = item.children?.filter(child => matches(child.title, child.key ?? routeKeys.get(child.to), section.heading, terms));
      return children?.length ? [{ ...item, children }] : [];
    });
    return items.length ? [{ ...section, items }] : [];
  });
}
export function navigationResultCount(sections: NavSection[]) {
  return sections.reduce((total, section) => total + section.items.reduce(
    (count, item) => count + (item.children?.length ?? 1), 0), 0);
}
